import React, { SelectHTMLAttributes, forwardRef, useCallback, useEffect, useRef } from 'react';

import { Group, Select, SelectItem } from '@mantine/core';
import { Font, FontList, FontManager, OPTIONS_DEFAULTS } from '@samuelmeuli/font-manager';

const DEFAULT_VALUE = 'Default';

interface Props extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
    onChange?: (fontFamily: string) => void;
}

export default function FontPicker({ onChange, value }: Props) {
    const fontManager = useRef<FontManager>();
    const [fonts, setFonts] = React.useState<FontList>();

    const handleChange = (fontFamily: string) => {
        if (!fontFamily || fontFamily === DEFAULT_VALUE) {
            fontManager.current?.setActiveFont('');
            onChange?.('');
        } else {
            fontManager.current?.setActiveFont(fontFamily);
        }
    };

    const onFontChange = useCallback(
        (font: Font) => {
            if (onChange) {
                onChange(font.family);
            }
        },
        [onChange]
    );

    useEffect(() => {
        if (!fontManager.current) {
            fontManager.current = new FontManager(
                process.env.REACT_APP_GOOGLE_FONTS_API_KEY || '',
                String(value),
                { ...OPTIONS_DEFAULTS, pickerId: 'fontPicker', sort: 'popularity', limit: 30 },
                onFontChange
            );
            if (!!value) {
                fontManager.current.addFont('', false);
            }
            fontManager.current.init().then(setFonts);
        }
    }, [onFontChange, value]);

    const options: SelectItem[] = Array.from(fonts?.entries() || []).map(([key, font]) => ({
        value: font.family,
        label: font.family,
        font,
    }));

    const ItemComponent = forwardRef<HTMLDivElement, SelectItem>(({ value, font, ...others }: SelectItem, ref) => (
        <div ref={ref} {...others}>
            <Group noWrap>
                <span
                    className="block truncate"
                    id={`font-button-${font.id}${fontManager.current?.selectorSuffix || ''}`}
                >
                    {font.family || DEFAULT_VALUE}
                </span>
            </Group>
        </div>
    ));

    return (
        <Select
            data={options}
            itemComponent={ItemComponent}
            searchable
            value={fontManager.current?.getActiveFont().family}
            onChange={handleChange}
            placeholder={DEFAULT_VALUE}
        />
    );
}
